import React from 'react';
import Stripe from 'stripe';
import { Button, Link, makeStyles } from '@material-ui/core';
import BaseTypography from '../../Text/BaseTypography';
import { PlansUtils } from '../../../utils';
import * as Colors from '../../../theme/colors';
import { usePlanInfo } from '../../../hooks/usePlanInfo';
import history from '../../../history';
import { PLAN_PAGE_CANCEL_START, SWITCH_TO_NEW_PLAN } from '../../../constants';
import { FlagsContext } from '../../../context';
import { SALES_EMAIL, SUPPORT_EMAIL } from '../../../constants/externalUrls';

const useStyles = makeStyles(() => ({
  contactLink: {
    color: Colors.BlackHeadings,
  },
  inlineButton: {
    verticalAlign: 'baseline',
  },
  cancelButton: {
    verticalAlign: 'baseline',
    minWidth: 0,
  },
}));

interface CurrentPlanProps {
  subscription?: Stripe.Subscription;
}

const CurrentPlan: React.FC<CurrentPlanProps> = ({ subscription }) => {
  const classes = useStyles();
  const { EnableNewPricingPlans } = React.useContext(FlagsContext);

  /**
   * Return a component that contains a message for user to select a plan
   */
  const renderNoPlanMessage = () => (
    <BaseTypography
      data-testid="no-plan-message"
      style={{ color: Colors.GraySmall }}
    >
      Please select a plan to continue using your portal. Reach out to{' '}
      <Link
        href={`mailto:${SALES_EMAIL}?subject=Choose Plan`}
        TypographyClasses={{ colorPrimary: classes.contactLink }}
      >
        <BaseTypography fontType="13Medium" component="span">
          {SALES_EMAIL}
        </BaseTypography>
      </Link>{' '}
      for help choosing a plan.
    </BaseTypography>
  );

  // if there is no active subscription then render noPlanMessage
  if (!subscription) {
    return renderNoPlanMessage();
  }

  const mainSubscriptionItem = subscription.items?.data[0];

  // if no plan is found for subscription then render noPlanMessage
  if (!mainSubscriptionItem) {
    return renderNoPlanMessage();
  }

  // extract plan info from subscription
  const {
    planName,
    currentPeriodEndDate,
    cancelAtPeriodEnd,
    isLegacyEnterprisePlan,
    isLegacyPlan,
  } = usePlanInfo();

  if (PlansUtils.isTrialPlan(mainSubscriptionItem.price)) {
    return (
      <BaseTypography style={{ color: Colors.GraySmall }} component="span">
        {PlansUtils.getTrialDaysRemainingText(subscription)}
      </BaseTypography>
    );
  }

  const contactUsLink = (
    <Link
      href={`mailto:${SUPPORT_EMAIL}?subject=Plan Cancellation`}
      TypographyClasses={{ colorPrimary: classes.contactLink }}
    >
      <BaseTypography component="span">{SUPPORT_EMAIL}</BaseTypography>
    </Link>
  );

  const openEnterpriseSupport = () => {
    const actionLink = `pageAction=contactSupport&body=${encodeURIComponent(
      SWITCH_TO_NEW_PLAN,
    )}`;
    history.replace({
      search: actionLink,
    });
  };

  const navigateToCancellationFlow = () =>
    history.push(PLAN_PAGE_CANCEL_START.path);

  const constructPlanMessages = () => {
    // Fixed access plans are not effective with new pricing plans
    if (
      !EnableNewPricingPlans &&
      PlansUtils.isFixedAccessPlan(mainSubscriptionItem.price)
    ) {
      return (
        <>
          Since you started a Portal plan before June 2021, you are on a
          discounted plan that is no longer available for new customers. If you
          would like to switch to our new pricing model, please contact us at{' '}
          {contactUsLink}
        </>
      );
    }

    if (isLegacyEnterprisePlan) {
      return (
        <span data-testid="old-enterprise-plan">
          {`You're currently on an enterprise plan. If you have any questions
          about your plan or billing, please contact your dedicated account
          manager.`}
        </span>
      );
    }

    if (isLegacyPlan) {
      return (
        <span data-testid="old-portal-plan">
          You’re currently on a discounted Early Access plan with reduced
          payment fees and other benefits. If you’d like to switch to one of our{' '}
          <Button
            color="primary"
            href="http://copilot.com/pricing"
            target="_blank"
            data-testid="new-pricing-plan-action-link"
            className={classes.inlineButton}
          >
            new plans
          </Button>
          , please{' '}
          <Button
            color="primary"
            onClick={openEnterpriseSupport}
            className={classes.inlineButton}
            component="span"
          >
            contact us
          </Button>
          . If you’d like to cancel and forfeit Early Access pricing, you can do
          that{' '}
          <Button
            color="primary"
            onClick={navigateToCancellationFlow}
            className={classes.cancelButton}
            component="span"
          >
            here
          </Button>
          .
        </span>
      );
    }

    if (PlansUtils.isEnterprisePlan(mainSubscriptionItem.price)) {
      return 'You are currently on a Portal enterprise plan. To make changes, please contact your account manager.';
    }

    let prePlanNameText = 'You are currently on the ';
    let prePeriodText = ', renewing on ';
    let postPeriodText = '';

    if (cancelAtPeriodEnd) {
      prePlanNameText = 'Your plan is scheduled to be canceled on ';
      prePeriodText = '';
      postPeriodText = ' You will not be charged again.';
    }

    return (
      <>
        {prePlanNameText}
        {!cancelAtPeriodEnd && (
          <BaseTypography fontType="13Medium" component="span">
            {planName}
          </BaseTypography>
        )}
        {prePeriodText}
        {currentPeriodEndDate && (
          <BaseTypography fontType="13Medium" component="span">
            {currentPeriodEndDate}
            {'. '}
          </BaseTypography>
        )}
        {postPeriodText}
      </>
    );
  };

  return (
    <BaseTypography style={{ color: Colors.GraySmall }}>
      {constructPlanMessages()}
    </BaseTypography>
  );
};

export default CurrentPlan;
